import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";

import { formatPhone } from "../../utils/formatPhone";

const useStyles = makeStyles(theme => ({
  search: { marginBottom: theme.spacing(1.5) },
  list: { maxHeight: "50vh", overflowY: "auto", margin: theme.spacing(0, -1) },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": { background: theme.app.selectedRow },
  },
  rowActive: { background: theme.app.selectedRow, outline: `2px solid ${theme.app.lime}` },
  num: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  desc: { fontSize: 12.5, color: theme.app.textTertiary },
  main: { flex: 1, minWidth: 0 },
  typeFixo: { backgroundColor: theme.app.chip, color: theme.app.textSecondary, fontWeight: 600, height: 22 },
  typeMovel: { backgroundColor: "#E6F4EA", color: "#1E7E34", fontWeight: 600, height: 22 },
  empty: { padding: theme.spacing(4), textAlign: "center", color: theme.app.textSecondary },
}));

// Picker for an app phone number. Default view = 10 most recent; search widens it.
// Warns + asks confirmation when picking a number already in use by ANOTHER app
// (publishing would steal it, breaking that app's inbound routing).
const NumberPickerModal = ({ open, numbers, value, currentAppId, onClose, onPick }) => {
  const classes = useStyles();
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null); // number awaiting "substituir" confirm

  // the app currently linked to a number, if it's NOT the app being edited
  const usedByOther = n =>
    n.inboundApplication && String(n.inboundApplication.id) !== String(currentAppId)
      ? n.inboundApplication
      : null;

  const choose = n => {
    if (usedByOther(n)) setPending(n);
    else onPick(n.id);
  };

  const recent = useMemo(
    () =>
      [...(numbers || [])].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      ),
    [numbers]
  );

  const term = q.trim().toLowerCase();
  const shown = term
    ? recent.filter(
        n =>
          formatPhone(n.number).toLowerCase().includes(term) ||
          String(n.number).includes(term.replace(/\D/g, "")) ||
          (n.city || "").toLowerCase().includes(term)
      )
    : recent.slice(0, 10);

  if (pending) {
    const app = usedByOther(pending);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Número já em uso</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>
            O número <b>{formatPhone(pending.number)}</b> já está vinculado ao fluxo{" "}
            <b>{app ? app.name : "outro"}</b>.
            <br />
            <br />
            Ao publicar este fluxo, o número será <b>movido para cá</b> e o fluxo{" "}
            <b>{app ? app.name : "anterior"}</b> deixará de receber chamadas nele.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const id = pending.id;
              setPending(null);
              onPick(id);
            }}
          >
            Mover para este fluxo
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Escolher número de telefone</DialogTitle>
      <DialogContent>
        <TextField
          className={classes.search}
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Buscar por número ou descrição…"
          value={q}
          onChange={e => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.list}>
          {/* QUALQUER NÚMERO: a conta recebe pelo tronco compartilhado da operadora,
              identificada pelo prefixo de roteamento — então dá para atender
              qualquer DID entregue com esse prefixo, sem cadastrar um a um. */}
          {!term && (
            <div
              className={`${classes.row} ${String(value) === "any" ? classes.rowActive : ""}`}
              onClick={() => onPick("any")}
            >
              <div className={classes.main}>
                <div className={classes.num}>Qualquer número</div>
                <div className={classes.desc}>
                  atende todas as chamadas entregues à sua conta, inclusive números novos
                </div>
              </div>
            </div>
          )}
          {shown.map(n => (
            <div
              key={n.id}
              className={`${classes.row} ${String(n.id) === String(value) ? classes.rowActive : ""}`}
              onClick={() => choose(n)}
            >
              <div className={classes.main}>
                <div className={classes.num}>{formatPhone(n.number)}</div>
                {usedByOther(n) ? (
                  <div className={classes.desc} style={{ color: "#B26A00" }}>
                    em uso por: {usedByOther(n).name}
                  </div>
                ) : n.city ? (
                  <div className={classes.desc}>{n.city}</div>
                ) : null}
              </div>
              <Chip
                size="small"
                label={n.type === "movel" ? "móvel" : "fixo"}
                className={n.type === "movel" ? classes.typeMovel : classes.typeFixo}
              />
            </div>
          ))}
          {shown.length === 0 && <div className={classes.empty}>Nenhum número encontrado.</div>}
        </div>
        {!term && recent.length > 10 && (
          <div className={classes.desc} style={{ marginTop: 8 }}>
            Mostrando os 10 mais recentes — use a busca para ver os demais.
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NumberPickerModal;
